import BasicButton from "../../components/Buttons/BasicButton";
import XsCont from "../../components/containers/Xs";
import BasicForm from "../../components/textField/BasicForm";
import PasswordField from "../../components/textField/PasswordField";
import { validationMessage } from "../../consts/ValidationMessage";
import { getUserPool } from "../../utils/cognitoUtils";
import {
  isAlphanumeric,
  isExactPasswordFormat,
  isValidateEmail,
  validateRequiredFields,
  ValidationErrors,
} from "../../utils/validationUtils";
import { Typography, Box } from "@mui/material";
import { CognitoUserAttribute } from "amazon-cognito-identity-js";
import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";

type SignUpInfoFormStepProps = {
  onNext: () => void;
};

interface CognitoError extends Error {
  code?: string;
}

const SignUpInfoFormStep: FC<SignUpInfoFormStepProps> = ({
  onNext,
}: SignUpInfoFormStepProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    loginId: "",
    password: "",
    confirmPassword: "",
    email: "",
  });

  const [errors, setErrors] = useState<ValidationErrors>({
    loginId: "",
    password: "",
    confirmPassword: "",
    email: "",
  });

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = (): boolean => {
    const requiredFields: string[] = [
      "loginId",
      "password",
      "confirmPassword",
      "email",
    ];
    const errorMessages = {
      loginId: `ログインID${validationMessage.REQUIRED}`,
      password: `パスワード${validationMessage.REQUIRED}`,
      confirmPassword: `パスワード（確認用）${validationMessage.REQUIRED}`,
      email: `メールアドレス${validationMessage.REQUIRED}`,
    };
    const newErrors = validateRequiredFields(
      form,
      requiredFields,
      errorMessages
    );
    const loginId: string = form.loginId;
    const password: string = form.password;
    const email: string = form.email;
    if (!newErrors.password && !newErrors.confirmPassword) {
      if (password !== form.confirmPassword) {
        newErrors.confirmPassword = validationMessage.PW_CONFIRM;
      }
      if (password.length < 10) {
        newErrors.password = validationMessage.PW_LENGTH;
      }
      if (password === loginId) {
        newErrors.password = validationMessage.PW_CONFIRM_LOGINID;
      }
      if (!isExactPasswordFormat(password)) {
        newErrors.password = validationMessage.PW_FORMAT;
      }
      if (!isValidateEmail(email)) {
        newErrors.email = validationMessage.EMAIL;
      }
    }
    if (loginId && !isAlphanumeric(loginId)) {
      newErrors.loginId = validationMessage.ID_FORMAT;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    try {
      if (validate()) {
        const userPool = getUserPool();
        userPool.signUp(
          form.loginId,
          form.password,
          [new CognitoUserAttribute({ Name: "email", Value: form.email })],
          [],
          (err): void => {
            if (err) {
              const cognitoError = err as CognitoError;
              if (cognitoError.code === "UsernameExistsException") {
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  loginId: "すでに存在するユーザ名です。",
                }));
                return;
              }
              navigate("/error");
              return;
            }
            const userDetailInfo = JSON.parse(
              sessionStorage.getItem("userDetailInfo") || "{}"
            );
            sessionStorage.setItem(
              "userDetailInfo",
              JSON.stringify({
                ...userDetailInfo,
                userId: form.loginId,
                password: form.password,
                email: form.email,
              })
            );
            onNext();
          }
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <XsCont>
      <Box mt={8} textAlign="center">
        <Typography variant="h5" gutterBottom>
          サインアップ
        </Typography>
      </Box>
      <Box
        component="form"
        mt={5}
        display="flex"
        flexDirection="column"
        gap={3}
        maxWidth="600px"
        mx="auto"
      >
        <BasicForm
          label={"ログインID"}
          name={"loginId"}
          value={form.loginId}
          onChange={handleChange}
          error={!!errors.loginId}
          helperText={errors.loginId}
        />
        <Typography variant="body2" color="textSecondary">
          <ul>
            <li>V Callプラグイン for kintone のサービスサイトにログインする ID をここに入力してください。</li>
            <li>{validationMessage.ID_FORMAT}</li>
          </ul>
        </Typography>
        <PasswordField
          label="パスワード"
          name="password"
          value={form.password}
          error={!!errors.password}
          helperText={errors.password}
          onChange={handleChange}
          showPassword={showPassword}
          onTogglePasswordVisibility={handleClickShowPassword}
        />
        <PasswordField
          label="パスワード（確認用）"
          name="confirmPassword"
          value={form.confirmPassword}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          onChange={handleChange}
          showPassword={showPassword}
          onTogglePasswordVisibility={handleClickShowPassword}
        />
        <Typography variant="body2" color="textSecondary">
          <ul>
            <li>{validationMessage.PW_LENGTH}</li>
            <li>{validationMessage.PW_FORMAT}</li>
          </ul>
        </Typography>
        <BasicForm
          label={"メールアドレス"}
          name={"email"}
          value={form.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
        />
      </Box>
      <Box
        mt={3}
        display="flex"
        flexDirection="column"
        maxWidth="600px"
        mx="auto"
      >
        <Box mt={1}>
          <BasicButton onClick={handleSubmit} mt={2}>
            メールアドレスに認証コードを送信
          </BasicButton>
        </Box>
      </Box>
    </XsCont>
  );
};

export default SignUpInfoFormStep;
